import { isHexColour } from '../styles/colour';
import type { SymbolInk } from './ink';

export type BackingShape = 'circle' | 'square' | 'rounded-square' | 'triangle';
/** Atomic authored style, shared by feature overrides and annotation markers. Absence means off. */
export interface SymbolBacking {
  readonly enabled: boolean;
  readonly shape: BackingShape;
  readonly fillColor: string;
  readonly fillOpacity: number;
  readonly borderColor: string;
  readonly borderOpacity: number;
  readonly borderWidth: number;
  /** Multiplier of the aspect-aware comfortable minimum, not of the legacy point radius. */
  readonly scale: number;
  /** Preferred outer logical pixels. Presence enables ink fitting; absence preserves legacy sizing. */
  readonly size?: number;
}
export interface SymbolPresentation {
  readonly backing?: SymbolBacking;
  readonly size?: number;
  readonly opacity?: number;
}
const fields = [
  'enabled',
  'shape',
  'fillColor',
  'fillOpacity',
  'borderColor',
  'borderOpacity',
  'borderWidth',
  'scale',
];
export function isSymbolBacking(value: unknown): value is SymbolBacking {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  const bounded = (n: unknown, min: number, max: number) =>
    typeof n === 'number' && Number.isFinite(n) && n >= min && n <= max;
  return (
    fields.every((k) => Object.hasOwn(v, k)) &&
    Object.keys(v).every((k) => fields.includes(k) || k === 'size') &&
    typeof v.enabled === 'boolean' &&
    ['circle', 'square', 'rounded-square', 'triangle'].includes(v.shape as string) &&
    typeof v.fillColor === 'string' &&
    isHexColour(v.fillColor) &&
    typeof v.borderColor === 'string' &&
    isHexColour(v.borderColor) &&
    bounded(v.fillOpacity, 0, 1) &&
    bounded(v.borderOpacity, 0, 1) &&
    bounded(v.borderWidth, 0, 8) &&
    bounded(v.scale, 1, 2.5) &&
    (v.size === undefined || (v.shape !== 'triangle' && bounded(v.size, 0, 2048)))
  );
}
export function defaultSymbolBacking(colour?: string): SymbolBacking {
  return {
    enabled: true,
    shape: 'circle',
    fillColor: '#ffffff',
    fillOpacity: 1,
    borderColor: colour && isHexColour(colour) ? colour.toLowerCase() : '#25313b',
    borderOpacity: 1,
    borderWidth: 1,
    scale: 1,
  };
}
export function changeBackingShape(
  current: SymbolBacking | undefined,
  shape: BackingShape | 'off',
  colour?: string,
  fit?: { readonly artwork: BackingArtwork; readonly foregroundSize: number },
): SymbolBacking {
  const previous = current ?? defaultSymbolBacking(colour);
  const next =
    shape === 'off' ? { ...previous, enabled: false } : { ...previous, enabled: true, shape };
  if (next.shape === 'triangle') delete next.size;
  if (!fit || shape === 'off' || shape === 'triangle') return next;
  if (!current) return { ...next, size: initialBackingSize(next, fit.artwork, fit.foregroundSize) };
  if (shape === current.shape) return next;
  const old = backingGeometry(current, fit.artwork, fit.foregroundSize);
  return {
    ...next,
    size:
      current.size ??
      (current.scale === 1
        ? initialBackingSize(next, fit.artwork, fit.foregroundSize)
        : Math.max(old.shapeWidth, old.shapeHeight) + current.borderWidth),
  };
}
export function backingKey(presentation?: SymbolPresentation): string {
  const b = presentation?.backing;
  if (!b?.enabled) return '';
  return JSON.stringify([
    b.shape,
    b.fillColor.toLowerCase(),
    b.fillOpacity,
    b.borderColor.toLowerCase(),
    b.borderOpacity,
    b.borderWidth,
    b.scale,
    ...(b.size === undefined ? [] : [b.size]),
    presentation?.size ?? 80,
    presentation?.opacity ?? 1,
  ]);
}
export interface BackingGeometry {
  readonly width: number;
  readonly height: number;
  readonly shapeWidth: number;
  readonly shapeHeight: number;
  readonly foreground: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
}
export interface BackingArtwork {
  readonly width: number;
  readonly height: number;
  readonly ink?: SymbolInk;
}
/** Minimum inner clearance stays modest at both small and large sizes. Stroke is accounted once. */
export function minimumBackingSize(
  backing: Pick<SymbolBacking, 'shape' | 'borderWidth'>,
  artwork: BackingArtwork,
  size: number,
): number {
  if (backing.shape === 'triangle')
    return backingGeometry({ ...defaultSymbolBacking(), ...backing }, artwork, size).width;
  const longEdge = Math.max(artwork.width, artwork.height);
  const ink = artwork.ink;
  const x =
    size * (ink ? Math.max(Math.abs(ink.left), Math.abs(ink.right)) : artwork.width / longEdge / 2);
  const y =
    size *
    (ink ? Math.max(Math.abs(ink.top), Math.abs(ink.bottom)) : artwork.height / longEdge / 2);
  const margin = Math.max(0.75, size * 0.015);
  const clearance = margin + backing.borderWidth / 2;
  if (backing.shape === 'circle') {
    return (
      2 *
        (size * (ink?.radius ?? Math.hypot(artwork.width, artwork.height) / longEdge / 2) +
          clearance) +
      backing.borderWidth
    );
  }
  if (backing.shape === 'square') return 2 * (Math.max(x, y) + clearance) + backing.borderWidth;
  // The whole ink rectangle clears the rounded corner's inward offset, not just the straight sides.
  let low = 2 * (Math.max(x, y) + clearance),
    high = low * 2;
  for (let i = 0; i < 40; i++) {
    const side = (low + high) / 2,
      radius = side * 0.15;
    const distance = Math.hypot(
      Math.max(0, x - side / 2 + radius),
      Math.max(0, y - side / 2 + radius),
    );
    if (distance + clearance <= radius) high = side;
    else low = side;
  }
  return high + backing.borderWidth;
}
export function initialBackingSize(
  backing: Pick<SymbolBacking, 'shape' | 'borderWidth'>,
  artwork: BackingArtwork,
  size: number,
): number {
  return minimumBackingSize(backing, artwork, size) + Math.max(2, size * 0.06);
}
/** Cubic quarter-circles match the Canvas rounded rectangle; no new PDF raster path. */
export function roundedSquarePath(side: number): string {
  const h = side / 2,
    r = side * 0.15,
    k = r * 0.5522847498307936,
    a = h - r;
  return `M${-a} ${-h} L${a} ${-h} C${a + k} ${-h} ${h} ${-a - k} ${h} ${-a} L${h} ${a} C${h} ${a + k} ${a + k} ${h} ${a} ${h} L${-a} ${h} C${-a - k} ${h} ${-h} ${a + k} ${-h} ${a} L${-h} ${-a} C${-h} ${-a - k} ${-a - k} ${-h} ${-a} ${-h} Z`;
}
/** Coordinates are centred on the combined bounds; triangle artwork sits at its usable centroid. */
export function backingGeometry(
  backing: SymbolBacking,
  aspect: BackingArtwork,
  size: number,
): BackingGeometry {
  const w = (size * aspect.width) / Math.max(aspect.width, aspect.height);
  const h = (size * aspect.height) / Math.max(aspect.width, aspect.height);
  if (backing.size !== undefined && backing.shape !== 'triangle') {
    const outer = Math.max(backing.size, minimumBackingSize(backing, aspect, size));
    const side = outer - backing.borderWidth;
    return {
      width: Math.max(w, outer),
      height: Math.max(h, outer),
      shapeWidth: side,
      shapeHeight: side,
      foreground: { x: -w / 2, y: -h / 2, width: w, height: h },
    };
  }
  const padding = size * 0.12 + backing.borderWidth;
  let shapeWidth: number,
    shapeHeight: number,
    foregroundY = -h / 2;
  if (backing.shape === 'circle')
    shapeWidth = shapeHeight = (Math.hypot(w, h) + padding * 2) * backing.scale;
  else if (backing.shape === 'square' || backing.shape === 'rounded-square')
    shapeWidth = shapeHeight = (size + padding * 2) * backing.scale;
  else {
    // Clear both the sloping edges and the base for tall artwork at the usable centroid.
    shapeWidth =
      Math.max(
        1.5 * (w + padding * 2) + (Math.sqrt(3) / 2) * (h + padding * 2),
        Math.sqrt(3) * (h + padding * 2),
      ) * backing.scale;
    shapeHeight = (shapeWidth * Math.sqrt(3)) / 2;
    foregroundY += shapeHeight / 6;
  }
  return {
    width: shapeWidth + backing.borderWidth,
    height: shapeHeight + backing.borderWidth,
    shapeWidth,
    shapeHeight,
    foreground: { x: -w / 2, y: foregroundY, width: w, height: h },
  };
}
export function backingCanvasPath(
  ctx: CanvasRenderingContext2D,
  backing: SymbolBacking,
  geometry: BackingGeometry,
) {
  const w = geometry.shapeWidth,
    h = geometry.shapeHeight;
  ctx.beginPath();
  if (backing.shape === 'circle') ctx.arc(0, 0, w / 2, 0, Math.PI * 2);
  else if (backing.shape === 'square') ctx.rect(-w / 2, -h / 2, w, h);
  else if (backing.shape === 'rounded-square') ctx.roundRect(-w / 2, -h / 2, w, h, w * 0.15);
  else {
    ctx.moveTo(0, -h / 2);
    ctx.lineTo(w / 2, h / 2);
    ctx.lineTo(-w / 2, h / 2);
    ctx.closePath();
  }
}
